/**
 * recentBots — "last opened" ordering for the workspace bot list in the
 * sidenav's home level.
 *
 * Per-browser only (localStorage). The user-preferences API stores a fixed
 * set of typed fields (see `user_preferences.go`, which rejects unknown keys),
 * and a recency map isn't worth a backend field — a wrong order on a new
 * device just means alphabetical-ish fallback.
 *
 * `markBotOpened` is called from NavProvider whenever a bot comes into scope,
 * so every route that carries a `botId` refreshes that bot's timestamp.
 */
import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'voxcore.recentBots.v1';
/** Cap the map so it can't grow without bound in a large workspace. */
const MAX_ENTRIES = 60;

export type RecentBots = Record<string, number>;

const EMPTY: RecentBots = {};

let cache: RecentBots | null = null;
const listeners = new Set<() => void>();

function read(): RecentBots {
  if (cache) return cache;
  if (typeof window === 'undefined') return (cache = EMPTY);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const out: RecentBots = {};
    if (parsed && typeof parsed === 'object') {
      for (const [id, ts] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof ts === 'number' && Number.isFinite(ts)) out[id] = ts;
      }
    }
    cache = out;
  } catch {
    cache = EMPTY;
  }
  return cache;
}

function write(next: RecentBots) {
  cache = next;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch { /* private mode / quota — recency is best-effort */ }
  }
  listeners.forEach((cb) => cb());
}

export function getRecentBots(): RecentBots {
  return read();
}

/** Stamp `botId` as just-opened. No-op for an empty id. */
export function markBotOpened(botId: string | undefined | null): void {
  const id = (botId || '').trim();
  if (!id) return;
  const current = read();
  const next: RecentBots = { ...current, [id]: Date.now() };
  const ids = Object.keys(next);
  if (ids.length > MAX_ENTRIES) {
    ids.sort((a, b) => (next[b] ?? 0) - (next[a] ?? 0));
    for (const stale of ids.slice(MAX_ENTRIES)) delete next[stale];
  }
  write(next);
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Re-renders the caller whenever the recency map changes. */
export function useRecentBots(): RecentBots {
  return useSyncExternalStore(subscribe, read, () => EMPTY);
}

/**
 * Most-recently-opened first. Bots that were never opened keep their incoming
 * relative order and sit after the ones that were (Array#sort is stable).
 */
export function sortByRecency<T>(items: T[], idOf: (item: T) => string, recent: RecentBots): T[] {
  return [...items].sort((a, b) => (recent[idOf(b)] ?? 0) - (recent[idOf(a)] ?? 0));
}

/** Test seam — drops the in-memory cache so the next read hits storage. */
export function resetRecentBotsCache(): void {
  cache = null;
}
